import { Component, AfterViewInit, ChangeDetectorRef, ViewChild } from '@angular/core';
import { BaseComponent } from '../../base/base.component';
import { Router } from '@angular/router';
import { ProjectStore } from '../../../services/stores/projects.store';
import { DashboardHelper } from '../../../services/dashboard.service';
import { AlertService } from '../../../services/alert.service';
import { NotificationType } from 'angular2-notifications';
import { ExternalLinkStore } from '../../../services/stores/external-link.store';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { DialogComponent } from '../../general/dialog/confirm/dialog.component';
import { AppModes } from '../../../modules/catch-core/data/enum/app-name.enum';
import { UserStore } from '../../../services/stores/user.store';
import { IProjectModel } from '../../../models/project/project.model';
import { WorkspaceStore } from '../../../services/stores/workspace.store';
import { ICustomColorScheme, IProjectAllocationChartData, IProjectStatusData, ICurrentWorkingProjectData, IProjectMetrics } from '../../../models/project/project-dashboard.model';
import { ProjectService } from '../../../services/project.service';
import { combineLatest, filter, map, take } from 'rxjs';
import { IBluePrintModel } from '../../../models/blueprint/blueprint.model';
import { List } from 'immutable';
import { STATUS_IN_PROGRESS, STATUS_NEW, STATUS_COMPLETED } from '../../../modules/catch-core/data/constants/filter.constants';
import { BlueprintStore } from '../../../services/stores/blueprint.store';
import { NextGenAppNavigationHelper } from '../../../services/helpers/nextgen-app-navigation.helper';
import * as AppConstants from '../../../modules/catch-core/data/constants/app.constants';
import { FIELD_VALUE_UNASSIGNED } from '../../../modules/catch-core/data/constants/blueprint.constants';
import * as PermissionConstants from '../../../modules/catch-core/data/constants/permission.constants';
import { PermissionService } from '../../../services/permission.service';

@Component({
  selector: 'app-projectsview',
  templateUrl: './projects-view.component.html',
  styleUrls: ['./projects-view.component.scss'],
  standalone: false,
})
export class ProjectsViewComponent extends BaseComponent implements AfterViewInit {
  // UI bind properties
  projectList: IProjectModel[] = [];
  sortedData!: IProjectModel[];
  dataSourcePaged!: MatTableDataSource<any>;
  projectAllocationChartData: IProjectAllocationChartData[] = [];
  projectStatusData!: IProjectStatusData;
  projectMetrics: IProjectMetrics = {
    averageCompletionDays: 0,
    newProjectMonthCount: 0,
    projectCompletionCountMonth: 0,
    projectCompletionCountYear: 0,
  };
  paginator!: MatPaginator;
  sort!: MatSort;
  public currentWorkingProjectData!: ICurrentWorkingProjectData;
  userList: any[] = [];
  allBluePrints: List<IBluePrintModel> | null = List([]);
  relevantBlueprints: IBluePrintModel[] = [];
  relevantProjectOwners: any[] = [];
  blueprintFilter: string[] = [];
  projectOwnerFilter: string[] = [];
  textFilter: string = '';
  public isLoading$ = this.projectStore.$isProjectListLoading;
  public isDetailedView: boolean = true;
  public colorTheme: ICustomColorScheme[] = [
    { name: STATUS_IN_PROGRESS, value: '#C1CFE9' },
    { name: STATUS_COMPLETED, value: '#6DBD4A' },
    { name: STATUS_NEW, value: '#6C7481' },
  ];
  //animals: IExternalLink[];
  @ViewChild(MatPaginator) set matPaginator(paginator: MatPaginator) {
    this.paginator = paginator;
    if (this.dataSourcePaged) {
      this.dataSourcePaged.paginator = paginator;
    }
  }

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this.sort = sort;
    if (this.dataSourcePaged) {
      this.dataSourcePaged.sort = sort;
    }
  }

  public addEntities = false;

  constructor(
    private userStore: UserStore,
    private projectStore: ProjectStore,
    private workspaceStore: WorkspaceStore,
    private bluePrintStore: BlueprintStore,
    private projectService: ProjectService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private externalLinkStore: ExternalLinkStore,
    private dashboardHelper: DashboardHelper,
    private matDialog: MatDialog,
    private nextGenAppNavigationHelper: NextGenAppNavigationHelper,
    private alertService: AlertService,
    private permissionService: PermissionService,
  ) {
    super();
  }

  // Project row actions are rendered for everyone and disabled-with-a-tooltip when the user lacks
  // the permission (rather than hidden), so the action is discoverable and the reason is explained.
  // The matching click handlers below re-check the same permission - the disabled state is styling
  // only (no pointer-events: none, or the tooltip could never fire on hover).
  public get canCreateProject(): boolean {
    return this.permissionService.hasPermission(PermissionConstants.PROJECT_CREATE);
  }

  public canUpdateProject(projectId: string): boolean {
    return !!projectId && this.permissionService.hasPermission(PermissionConstants.PROJECT_UPDATE, projectId);
  }

  public canDeleteProject(projectId: string): boolean {
    return !!projectId && this.permissionService.hasPermission(PermissionConstants.PROJECT_DELETE, projectId);
  }

  public canReadAttachments(projectId: string): boolean {
    return !!projectId && this.permissionService.hasPermission(PermissionConstants.ATTACHMENT_READ, projectId);
  }

  ngAfterViewInit(): void {
    this.dashboardHelper.setShowLeftSidePanel(false);
    this.dashboardHelper.setShowGraphPanel(false);
    // this.dashboardHelper.setCurrentFilters(null);
    this.workspaceStore.searchFilter = null;
    this.dashboardHelper.setCurrentAppMode(AppModes.EnterpriseTester);
    this.cdr.detectChanges();

    this.subscribers.dataSubscription = combineLatest([this.projectStore.$projects, this.userStore.$projectOwnerCandidates, this.bluePrintStore.$allBluePrints, this.projectService.projectAllocationSelectedOwner]).subscribe(
      ([projects, users, blueprints, selectedOwner]) => {
        if (projects && users) {
          this.allBluePrints = blueprints;
          this.projectList = projects.toArray().filter((p) => p?.id && this.permissionService.hasPermission(PermissionConstants.PROJECT_READ, p.id));
          this.userList = users.toArray();
          this.prepareProjectData();

          if (selectedOwner && selectedOwner.fullName) {
            const user = this.userList.find((u) => `${u.firstName} ${u.lastName}` === selectedOwner.fullName);
            if (user) {
              this.projectOwnerFilter = [user.id];
              this.isDetailedView = true;
              if (this.dataSourcePaged) {
                this.updateDataSourceFilter();
              }
              // Reset the selection in service to stop it from triggering again on next load
              this.projectService.setProjectAllocationSelectedOwner(null);
            } else if (selectedOwner.fullName.toLowerCase() === FIELD_VALUE_UNASSIGNED.toLowerCase()) {
              this.projectOwnerFilter = [FIELD_VALUE_UNASSIGNED];
              this.isDetailedView = true;
              if (this.dataSourcePaged) {
                this.updateDataSourceFilter();
              }
              this.projectService.setProjectAllocationSelectedOwner(null);
            } else if (selectedOwner?.fullName?.toLowerCase() === 'inactive user') {
              const inactiveUserIds = (this.userList || [])
                .filter((u) => u && u.isActive === false && u.id)
                .map((u) => u.id);
              if (inactiveUserIds && inactiveUserIds.length > 0) {
                this.projectOwnerFilter = inactiveUserIds;
                this.isDetailedView = true;
                if (this.dataSourcePaged) {
                  this.updateDataSourceFilter();
                }
              }
              this.projectService.setProjectAllocationSelectedOwner(null);
            }
          }

          this.cdr.detectChanges();
        }
      },
      (err) => {
        console.error(err);
      },
    );

    this.subscribers.firstExternalLink = this.externalLinkStore.$firstExternalLink.subscribe((g) => {
      if (g) {
        const externalLinks = g.externalLinks;

        // remove hidden links
        // const nextGenexternalLinks = externalLinks.filter((e : any) => e.isNextGenMenu && e.active);

        // if (this.appTypeSubscription !== null) {
        //   this.appTypeSubscription.unsubscribe();
        // }
        //this.animals = this.dashboardHelper.buildSortedApplicationArray(nextGenexternalLinks);

        // by default set loaded app as smartmap
        // this.clickedItem = nextGenexternalLinks.find(n => n.productType.toLocaleLowerCase() === 'smartmap').productType;

        // hard coded to open map when load will be reomve in future
        // this.onLoadEtApp(externalLinks.find(e => e.productType === 'SmartMap' && e.active === true && e.isNextGenMenu === true));
      }
    });
  }

  getProjectOwner(userId: string) {
    if (this.userList?.length > 0 && userId) {
      const userObj = this.userList.find((ul) => ul?.id === userId);
      if (userObj) {
        const firstName = userObj.firstName || '';
        const lastName = userObj.lastName || '';
        const suffix = userObj.isActive ? '' : ' (Inactive)';
        return `${firstName} ${lastName}${suffix}`.trim();
      }
    }
    return FIELD_VALUE_UNASSIGNED;
  }

  isUserActive(userId: string) {
    const user = this.userList.find((u) => u.id === userId);
    return user && user.isActive;
  }

  applyFilter(event: Event) {
    this.textFilter = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.updateDataSourceFilter();
  }

  applyTextFilter() {
    this.updateDataSourceFilter();
  }

  onSearchClear() {
    this.textFilter = '';
    this.updateDataSourceFilter();
  }

  onClearAllFilters() {
    this.textFilter = '';
    this.blueprintFilter = [];
    this.projectOwnerFilter = [];
    this.updateDataSourceFilter();
  }

  applyBlueprintFilter() {
    this.updateDataSourceFilter();
  }

  applyProjectOwnerFilter() {
    this.updateDataSourceFilter();
  }

  private updateDataSourceFilter() {
    this.dataSourcePaged.filter = JSON.stringify({
      text: this.textFilter.toLowerCase(),
      blueprints: this.blueprintFilter,
      owners: this.projectOwnerFilter,
    });
  }

  public getBlueprintFilterDisplayName(): string {
    if (this.blueprintFilter.length === 0) {
      return 'Template';
    }
    if (this.blueprintFilter.length === 1) {
      const blueprint = this.relevantBlueprints.find((bp) => bp.id === this.blueprintFilter[0]);
      return `Template = ${blueprint ? blueprint.name : ''}`;
    }
    return `Template = ${this.blueprintFilter.length} selected`;
  }

  public getOwnerFilterDisplayName(): string {
    if (this.projectOwnerFilter.length === 0) {
      return 'Project Owner';
    }
    if (this.projectOwnerFilter.length === 1) {
      if (this.projectOwnerFilter[0] === FIELD_VALUE_UNASSIGNED) {
        return `Project Owner = ${FIELD_VALUE_UNASSIGNED}`;
      }
      const owner = this.relevantProjectOwners.find((o) => o.id === this.projectOwnerFilter[0]);
      return `Project Owner = ${owner ? owner.firstName + ' ' + owner.lastName + (owner.isActive ? '' : ' (Inactive)') : ''}`;
    }
    return `Project Owner = ${this.projectOwnerFilter.length} selected`;
  }

  public toggleView() {
    this.isDetailedView = !this.isDetailedView;
    if (!this.isDetailedView) {
      this.prepareProjectData();
    } else {
      setTimeout(() => {
        if (this.dataSourcePaged) {
          this.dataSourcePaged.paginator = this.paginator;
          this.dataSourcePaged.sort = this.sort;
          this.cdr.detectChanges();
        }
      });
    }
  }

  onProjectAdd() {
    if (!this.canCreateProject) {
      return;
    }
    this.dashboardHelper.setShowRightPanel(true);
    //this.dashboardHelper.setCurrentFilters(null);
    this.workspaceStore.searchFilter = null;
    this.router.navigate(
      [
        '/',
        'dashboard',
        {
          outlets: {
            rightPanel: ['addeditproject'],
            tabPanel: ['blank'],
          },
        },
      ],
      { queryParamsHandling: 'preserve' },
    );
  }

  onLoadProject(projectId: string) {
    if (!projectId || !this.permissionService.hasPermission(PermissionConstants.PROJECT_READ, projectId)) {
      return;
    }
    this.dashboardHelper.setCurrentProject(projectId);
    this.dashboardHelper.setSelectedItem('');
    //this.dashboardHelper.setCurrentFilters(null);
    this.workspaceStore.searchFilter = null;
    this.projectStore.loadProject(projectId, false);

    if (this.subscribers.projectLoaded) {
      this.subscribers.projectLoaded.unsubscribe();
    }
    this.subscribers.projectLoaded = this.projectStore.$selectedProject.pipe(
      filter((project) => !!project && project.id === projectId),
      take(1),
    ).subscribe((project) => {
      const reportApp = this.externalLinkStore.getAppLink(AppConstants.CATCH_INTERNAL_APP_REPORT_LOWER);
      if (reportApp) {
        // Find Backlog map ID first
        let backlogMapId = this.findBacklogMapId(project.treeNodes);
        if (!backlogMapId && project.treeNodes && project.treeNodes.length > 0) {
          backlogMapId = project.treeNodes[0].id;
        }
        this.dashboardHelper.setSelectedItem(backlogMapId);
        this.workspaceStore.selectedWorkspaceId = backlogMapId;
        this.nextGenAppNavigationHelper.loadNextgenApplication(reportApp);
        this.dashboardHelper.setShowGraphPanel(false);
      }
    });

    this.dashboardHelper.setShowLeftSidePanel(true);
    this.dashboardHelper.setShowRightPanel(false);
    this.dashboardHelper.setSharedRightPanelHeaderTitle('');
  }

  private findBacklogMapId(nodes: any[]): string {
    if (!nodes || nodes.length === 0) {
      return '';
    }
    for (const node of nodes) {
      if (node.name.toLowerCase() === AppConstants.BACKLOG_NODE_NAME_LOWER && node.type === 'map') {
        return node.id;
      }
      if (node.children && node.children.length > 0) {
        const id = this.findBacklogMapId(node.children);
        if (id) {
          return id;
        }
      }
    }
    return '';
  }

  public onManageAttachments(e: any, projectId: string): void {
    // stopPropagation first: a click on a disabled action must not fall through to the row's
    // (click)="onLoadProject(...)" handler.
    e.stopPropagation();
    if (!this.canReadAttachments(projectId)) {
      return;
    }
    this.dashboardHelper.setShowRightPanel(true);
    this.router.navigate(
      [
        '/',
        'dashboard',
        {
          outlets: {
            rightPanel: ['project-attachments', projectId],
            tabPanel: ['blank'],
          },
        },
      ],
      { queryParamsHandling: 'preserve' },
    );
  }

  public onCloneProject(e: any, projectId: string): void {
    e.stopPropagation();
    if (!this.canCreateProject) {
      return;
    }
    this.dashboardHelper.setShowRightPanel(true);
    this.router.navigate(
      [
        '/',
        'dashboard',
        {
          outlets: {
            rightPanel: ['copy-project', projectId],
            tabPanel: ['blank'],
          },
        },
      ],
      { queryParamsHandling: 'preserve' },
    );
  }

  public onEditProject(e: any, projectId: string): void {
    e.stopPropagation();
    if (!this.canUpdateProject(projectId)) {
      return;
    }
    this.dashboardHelper.setShowRightPanel(true);
    this.router.navigate(
      [
        '/',
        'dashboard',
        {
          outlets: {
            rightPanel: ['addeditproject', projectId],
            tabPanel: ['blank'],
          },
        },
      ],
      { queryParamsHandling: 'preserve' },
    );
  }

  public onDeleteProject(e: any, project: IProjectModel): void {
    e.stopPropagation();
    if (!this.canDeleteProject(project?.id)) {
      return;
    }
    let dialogRef: MatDialogRef<DialogComponent, any>;
    let confirmTextLabel = '';
    let dialogContent = '';

    confirmTextLabel = 'yes, delete everything';

    dialogContent =
      'This action will delete all the maps, items and packages under : <span class=deleteItem>' +
      project.name +
      '</span> to continue type <span class=deleteText>yes, delete everything</span> in the box below:';

    dialogRef = this.matDialog.open(DialogComponent, {
      width: '600px',
      minHeight: '200px',
      data: {
        title: 'Confirm Project Deletion',
        content: dialogContent,
        confirmText: confirmTextLabel,
        confirmButtonColor: 'warn',
        action: 'delete',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (result === confirmTextLabel) {
          this.projectStore.$deleteProject(project.id).subscribe({
            next: (res) => {
              if (res) {
                this.alertService.alert(NotificationType.Success, `Project "${project.name}" deleted successfully`, true);
              } else {
                this.alertService.showErrorMessage(`Failed to delete project "${project.name}"`);
              }
            },
            error: () => {
              this.alertService.showErrorMessage(`Error deleting project "${project.name}"`);
            },
          });
        }
      }
    });
  }

  private prepareProjectData(): void {
    if (!this.projectList) {
      console.warn('Project list is empty. Skipping data preparation.');
      return;
    }

    if (this.userList.length === 0 || !this.userList) {
      console.warn('User list is empty. This may affect project allocation data.');
    }

    try {
      const allProjectData = this.projectService.prepareAllProjectData(this.projectList, this.userList);

      if (allProjectData) {
        this.projectAllocationChartData = allProjectData.projectAllocationChartData || [];
        this.projectStatusData = allProjectData.projectStatusData || null;
        this.currentWorkingProjectData = allProjectData.currentWorkingProjectData || null;
        this.projectMetrics = allProjectData.projectMetrics || {
          averageCompletionDays: 0,
          newProjectMonthCount: 0,
          projectCompletionCountMonth: 0,
          projectCompletionCountYear: 0,
        };
        const SORT_HEADER_INTEGRATED_APPS = 'integratedapps';
        const SORT_HEADER_PROJECT_OWNER = 'projectowner';
        const SORT_HEADER_CREATED_DATE = 'createddate';
        const SORT_HEADER_MODIFIED_DATE = 'modifieddate';
        const SORT_HEADER_PERCENTAGE_COMPLETE = 'percentagecomplete';
        const SORT_HEADER_BLUE_PRINT = 'blueprint';
        this.dataSourcePaged = new MatTableDataSource(this.projectList);
        this.dataSourcePaged.sortingDataAccessor = (data: any, sortHeaderId: string): string | number => {
          // Convert sortHeaderId to lowercase for case-insensitive comparison
          const headerId = sortHeaderId.toLowerCase();

          switch (headerId) {
            case SORT_HEADER_INTEGRATED_APPS:
              return data.integratedApps[0]?.mappedProject?.name?.toLowerCase() || '';
            case SORT_HEADER_PROJECT_OWNER:
              return this.getProjectOwner(data.owner)?.toLowerCase() || '';
            case SORT_HEADER_CREATED_DATE:
            case SORT_HEADER_MODIFIED_DATE:
              return new Date(data[sortHeaderId]).getTime(); // Sorting by date
            case SORT_HEADER_PERCENTAGE_COMPLETE:
              return data[sortHeaderId] || 0; // Sorting by percentage as number
            case SORT_HEADER_BLUE_PRINT:
              const blueprint = this.allBluePrints?.find((bp) => bp?.id === data.bluePrintId);
              return blueprint?.name?.toLowerCase() || '';
            default:
              return typeof data[sortHeaderId] === 'string' ? data[sortHeaderId].toLowerCase() : data[sortHeaderId] || '';
          }
        };

        this.dataSourcePaged.filterPredicate = (data: any, filter: string): boolean => {
          const searchTerms = JSON.parse(filter);
          // const projectFields = [data.name, data.key, data.description].map((f) => f?.toLowerCase() || '');
          // const blueprint = this.allBluePrints?.find((bp) => bp != null && bp.id === data.bluePrintId);
          // const blueprintName = blueprint?.name?.toLowerCase() || '';
          // const projectOwnerName = this.getProjectOwner(data.owner)?.toLowerCase() || '';

          const matchesText = !searchTerms.text || (data.name ?? '').toLowerCase().includes(searchTerms.text) || (data.description ?? '').toLowerCase().includes(searchTerms.text);

          const matchesBlueprint = searchTerms.blueprints.length === 0 || searchTerms.blueprints.includes(data.bluePrintId);

          const isKnownUser = data?.owner && Array.isArray(this.userList) && this.userList.some((u) => u?.id === data.owner);
          const resolvedOwner = isKnownUser ? data.owner : FIELD_VALUE_UNASSIGNED;
          const matchesOwner = !searchTerms?.owners || searchTerms.owners.length === 0 || searchTerms.owners.includes(resolvedOwner);

          return matchesText && matchesBlueprint && matchesOwner;
        };

        const usedBlueprintIds = new Set(this.projectList.map((p) => p.bluePrintId));
        this.relevantBlueprints =
          this.allBluePrints
            ?.filter((bp) => bp != null && usedBlueprintIds.has(bp.id))
            .toArray()
            .sort((a, b) => a.name.localeCompare(b.name)) || [];

        const projectOwnerIds = new Set((this.projectList || []).map((p) => {
          const isKnown = p?.owner && Array.isArray(this.userList) && this.userList.some((u) => u?.id === p.owner);
          return isKnown ? p.owner : FIELD_VALUE_UNASSIGNED;
        }));
        this.relevantProjectOwners = (this.userList || []).filter((user) => user?.id && projectOwnerIds.has(user.id)).sort((a, b) => `${a?.firstName || ''} ${a?.lastName || ''}`.localeCompare(`${b?.firstName || ''} ${b?.lastName || ''}`));

        if (projectOwnerIds.has(FIELD_VALUE_UNASSIGNED)) {
          this.relevantProjectOwners.push({ id: FIELD_VALUE_UNASSIGNED, firstName: FIELD_VALUE_UNASSIGNED, lastName: '', isActive: true });
        }

        // (data, header) => data[header];
        this.dataSourcePaged.paginator = this.paginator;
        this.dataSourcePaged.sort = this.sort;
        // this.isLoading = false;
        this.updateDataSourceFilter();
        this.sort.sortChange.emit();
        this.cdr.detectChanges();
      } else {
        console.error('prepareAllProjectData returned null or undefined');
      }
    } catch (error) {
      console.error('Error preparing project data:', error);
    }
  }
}
